import { createRouter, createWebHistory } from 'vue-router';
import LandingPage from '../views/LandingPage.vue';
import LoginPage from '../views/LoginPage.vue';
import NameSetup from '../views/NameSetup.vue';
import WorkshopsList from '../views/WorkshopsList.vue';
import WorkshopBoard from '../views/WorkshopBoard.vue';

const routes = [
  {
    path: '/',
    name: 'landing',
    component: LandingPage,
    meta: { public: true },
  },
  {
    path: '/login',
    name: 'login',
    component: LoginPage,
    meta: { requiresGuest: true },
  },
  {
    path: '/setup-name',
    name: 'name-setup',
    component: NameSetup,
    meta: { requiresAuth: true },
  },
  {
    path: '/workshops',
    name: 'workshops',
    component: WorkshopsList,
    meta: { requiresAuth: true },
  },
  {
    path: '/invite/:code',
    name: 'invite',
    component: WorkshopsList,
    meta: { requiresAuth: true },
  },
  {
    path: '/workshop/:slug',
    name: 'workshop',
    component: WorkshopBoard,
    meta: { requiresAuth: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Auth navigation guards
router.beforeEach(async (to, from, next) => {
  const JWT_STORAGE_KEY = '__convexAuthJWT';
  const REFRESH_TOKEN_STORAGE_KEY = '__convexAuthRefreshToken';
  const convexUrl = import.meta.env.VITE_CONVEX_URL ?? '';

  const getStorageKey = (key: string): string => {
    if (!convexUrl) return key;
    const escapedNamespace = convexUrl.replace(/[^a-zA-Z0-9]/g, '');
    return `${key}_${escapedNamespace}`;
  };

  const storageGet = (key: string): string | null => {
    try {
      return localStorage.getItem(getStorageKey(key));
    } catch {
      return null;
    }
  };

  // Check if URL has a magic link code
  const hasCodeInUrl = typeof window !== 'undefined' && window.location.search.includes('code=');

  const getInviteToken = (): string | undefined => {
    const queryInvite = to.query.invite;
    if (typeof queryInvite === 'string' && queryInvite.length > 0) {
      return queryInvite;
    }

    if (to.name === 'invite') {
      const codeParam = to.params.code;
      if (typeof codeParam === 'string' && codeParam.length > 0) {
        return codeParam;
      }
    }

    return undefined;
  };

  const checkAuth = async (): Promise<{
    authenticated: boolean;
    needsNameSetup?: boolean;
  }> => {
    const storedToken = storageGet(JWT_STORAGE_KEY);
    if (!storedToken) return { authenticated: false };

    try {
      const { ConvexHttpClient } = await import('convex/browser');
      const client = new ConvexHttpClient(convexUrl);
      client.setAuth(storedToken);

      const api = (await import('../../convex/_generated/api')).api;

      // Check currentUser to see if name is set
      const currentUser = await client.query(api.auth.currentUser);
      const needsNameSetup = !!(currentUser && !currentUser.name);

      return { authenticated: true, needsNameSetup };
    } catch (error: any) {
      const errorMessage = error?.message || '';
      const isAuthError =
        errorMessage.includes('Unauthenticated') ||
        errorMessage.includes('Unauthorized') ||
        errorMessage.includes('401') ||
        errorMessage.includes('expired');

      // Only try refresh token for auth errors (not network errors)
      if (isAuthError) {
        const storedRefreshToken = storageGet(REFRESH_TOKEN_STORAGE_KEY);
        if (storedRefreshToken) {
          try {
            const { ConvexHttpClient } = await import('convex/browser');
            const client = new ConvexHttpClient(convexUrl);

            const result = (await client.action(
              'auth:signIn' as never,
              { refreshToken: storedRefreshToken } as never
            )) as { tokens?: { token: string; refreshToken: string } | null };

            if (result.tokens) {
              localStorage.setItem(getStorageKey(JWT_STORAGE_KEY), result.tokens.token);
              if (result.tokens.refreshToken) {
                localStorage.setItem(
                  getStorageKey(REFRESH_TOKEN_STORAGE_KEY),
                  result.tokens.refreshToken
                );
              }
              return { authenticated: true };
            }
          } catch {
            // Refresh failed, will clear tokens below
          }
        }
      } else {
        // For non-auth errors (network, etc.), assume we're still authenticated
        return { authenticated: true };
      }

      localStorage.removeItem(getStorageKey(JWT_STORAGE_KEY));
      localStorage.removeItem(getStorageKey(REFRESH_TOKEN_STORAGE_KEY));
      return { authenticated: false };
    }
  };

  // Protected routes require auth
  // BUT: If there's a magic link code in URL, let the page load so useConvexAuth can process it
  if (to.meta.requiresAuth && !hasCodeInUrl) {
    const inviteToken = getInviteToken();
    const authResult = await checkAuth();
    if (!authResult.authenticated) {
      if (inviteToken) {
        return next({ path: '/login', query: { invite: inviteToken } });
      }
      return next('/login');
    }
    // Redirect to name setup if name not set
    if (authResult.needsNameSetup && to.name !== 'name-setup' && from.name !== 'name-setup') {
      // Preserve invite token when redirecting to name setup
      if (inviteToken) {
        return next({ path: '/setup-name', query: { invite: inviteToken } });
      }
      return next('/setup-name');
    }
  }

  // Guest routes (login) redirect if already auth
  if (to.meta.requiresGuest) {
    const storedToken = storageGet(JWT_STORAGE_KEY);
    if (storedToken) {
      const authResult = await checkAuth();
      if (authResult.authenticated) {
        if (authResult.needsNameSetup) {
          // Preserve invite token when redirecting to name setup
          const inviteToken = getInviteToken();
          if (inviteToken) {
            return next({ path: '/setup-name', query: { invite: inviteToken } });
          }
          return next('/setup-name');
        }
        // Preserve invite token when redirecting to workshops
        const inviteToken = getInviteToken();
        if (inviteToken) {
          return next({ path: `/invite/${encodeURIComponent(inviteToken)}` });
        }
        return next('/workshops');
      }
    }
  }

  next();
});

export default router;
